/**
 * Batch H2 — Holder benefit list (shared by Claim detail, My Claims, Utilities).
 * Every status shows a plain-language reason; only "available" can be redeemed.
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle2, Lock, Clock, Gift, XCircle, Sparkles } from 'lucide-react';
import theme from '../../theme';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const BENEFIT_STATUS = {
  available: { label: 'Available', color: '#065F46', bg: '#ECFDF5', Icon: CheckCircle2 },
  locked: { label: 'Locked', color: '#92400E', bg: '#FFFBEB', Icon: Lock },
  scheduled: { label: 'Upcoming', color: '#1E40AF', bg: '#EFF6FF', Icon: Clock },
  redeemed: { label: 'Redeemed', color: '#5B6472', bg: '#F3F4F6', Icon: CheckCircle2 },
  sold_out: { label: 'All claimed', color: '#5B6472', bg: '#F3F4F6', Icon: XCircle },
  expired: { label: 'Ended', color: '#5B6472', bg: '#F3F4F6', Icon: XCircle },
  not_configured: { label: 'Not set up yet', color: '#5B6472', bg: '#F3F4F6', Icon: Lock },
};

function BenefitRow({ b, portfolioId, onRedeemed, compact }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const navigate = useNavigate();
  const st = BENEFIT_STATUS[b.status] || BENEFIT_STATUS.locked;
  const Icon = b.is_surprise && b.status !== 'available' ? Sparkles : st.Icon;
  const tid = b.association_key.replace(/[^a-zA-Z0-9-]/g, '-');

  const redeem = async () => {
    setBusy(true); setErr(null);
    try {
      const res = await axios.post(`${API}/buyer/benefits/redeem`, { portfolio_id: portfolioId, association_key: b.association_key, request_id: crypto.randomUUID() },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      toast.success('Redemption requested — the provider confirms fulfilment');
      onRedeemed && onRedeemed(res.data);
    } catch (e) {
      const d = e?.response?.data?.detail;
      setErr(typeof d === 'string' ? d : (d?.errors || []).join(' ') || 'Could not redeem');
    } finally { setBusy(false); }
  };

  return (
    <div data-testid={`benefit-${tid}`} style={{ padding: 12, borderRadius: 10, border: `1px solid ${theme.colors.borders}`, background: '#fff' }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <Icon size={16} color={st.color} style={{ marginTop: 2 }} />
        <div style={{ flex: 1, minWidth: 200 }}>
          <div style={{ fontWeight: 700, fontSize: 14, color: theme.colors.text.primary }}>{b.title}</div>
          {!compact && b.description && <div style={{ fontSize: 12, color: theme.colors.text.secondary, marginTop: 2 }}>{b.description}</div>}
          {(b.reasons || []).length > 0 && (
            <div data-testid={`benefit-reason-${tid}`} style={{ fontSize: 12, color: st.color, marginTop: 4 }}>{b.reasons.join(' · ')}</div>
          )}
          {!compact && b.status === 'available' && b.fulfillment_instructions && (
            <div style={{ fontSize: 12, color: theme.colors.text.muted, marginTop: 4 }}>How to use: {b.fulfillment_instructions}</div>
          )}
          {!compact && b.fulfillment_party && <div style={{ fontSize: 11, color: theme.colors.text.muted, marginTop: 2 }}>Fulfilled by {b.fulfillment_party}</div>}
          {err && <div data-testid={`benefit-error-${tid}`} style={{ fontSize: 12, color: theme.colors.status.error, marginTop: 4 }}>{err}</div>}
        </div>
        <span data-testid={`benefit-status-${tid}`} style={{ padding: '2px 8px', borderRadius: 999, background: st.bg, color: st.color, fontSize: 10, fontWeight: 800, textTransform: 'uppercase' }}>{st.label}</span>
        {portfolioId && (
          <button data-testid={`benefit-details-${tid}`} onClick={(e) => { e.stopPropagation(); navigate(`/buyer/benefits/${portfolioId}/${encodeURIComponent(b.association_key)}`); }}
            style={{ padding: '6px 12px', borderRadius: 8, border: `1px solid ${theme.colors.borders}`, background: '#fff', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>
            Details
          </button>
        )}
        {!compact && b.status === 'available' && portfolioId && (
          <button data-testid={`benefit-redeem-${tid}`} disabled={busy} onClick={redeem}
            style={{ padding: '6px 12px', borderRadius: 8, border: 'none', background: theme.colors.primary, color: '#fff', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>
            {busy ? 'Recording…' : 'Redeem'}
          </button>
        )}
      </div>
    </div>
  );
}

export default function BenefitList({ benefits, portfolioId, onRedeemed, compact = false }) {
  if (!benefits || benefits.length === 0) {
    return (
      <div data-testid="benefits-empty" style={{ fontSize: 12, color: theme.colors.text.muted, display: 'flex', gap: 6, alignItems: 'center' }}>
        <Gift size={14} /> The creator hasn't attached any benefits to this Kickback yet.
      </div>
    );
  }
  return (
    <div data-testid="benefit-list" style={{ display: 'grid', gap: 8 }}>
      {benefits.map(b => <BenefitRow key={b.association_key} b={b} portfolioId={portfolioId} onRedeemed={onRedeemed} compact={compact} />)}
    </div>
  );
}
